import { isAfter } from 'date-fns';
import { eventChannel, END } from 'redux-saga';
import { call, put, delay, select, take, takeLatest } from 'redux-saga/effects';

import { PREVIEW } from 'constants/Parameters';
import { getEntity } from 'modules/entity/selectors';
import { openRefreshPageModal } from 'modules/app/actions';
import { FETCH_ENTITY_SUCCESS } from 'modules/entity/actionTypes';
import { isConnected as isConnectedSelector } from 'modules/app/selectors';
import { MODULES } from 'modules/entity/subEntities/intervention/constants';

import * as actionTypes from './actionTypes';
import { KEEP_ALIVE_INTERVAL } from './constants';
import { initialize, send as socketSend } from './actions';
import { getTimeoutAt, getWebsocketUrl } from './selectors';

/**
 * @param {string} url
 * @returns {WebSocket}
 */
const createWebSocketConnection = url =>
  new Promise((resolve, reject) => {
    const socket = new WebSocket(url);

    socket.addEventListener('open', () => resolve(socket), { once: true });
    socket.addEventListener('error', reject, { once: true });
  });

/**
 * @param {WebSocket} socket
 * @returns {import('redux-saga').EventChannel}
 */
const createSocketChannel = socket =>
  eventChannel(emit => {
    socket.addEventListener('message', ({ data }) => {
      try {
        const socketData = JSON.parse(data);
        emit({ type: `SOCKET/${socketData.action?.toUpperCase() ?? 'RESPONSE'}`, payload: socketData.data });
      } catch (err) {
        emit({ type: 'SOCKET/ERROR', payload: err });
      }
    });

    socket.addEventListener(
      'close',
      ({ reason }) => {
        emit({ type: actionTypes.SOCKET_CLOSED, payload: reason });
        emit(END);
      },
      { once: true }
    );
    socket.addEventListener('error', ({ reason }) => emit({ type: 'SOCKET/ERROR', payload: reason }));

    return () => socket.close();
  });

/** @type {WebSocket} */
let socket;

function* isConnectionTimeout() {
  const timeoutAt = yield select(getTimeoutAt);
  return isAfter(new Date(), timeoutAt);
}

function* keepAlive() {
  let hasTimeout;
  do {
    yield put(socketSend('ping'));
    yield delay(KEEP_ALIVE_INTERVAL);
    hasTimeout = yield call(isConnectionTimeout);
  } while (!hasTimeout);
}

function* getWS() {
  const websocketUrl = yield select(getWebsocketUrl);
  const hasSocket = socket && socket.readyState !== socket.CLOSING && socket.readyState !== socket.CLOSED;
  const createSocket = !hasSocket && websocketUrl;
  if (createSocket) {
    try {
      socket = yield call(createWebSocketConnection, websocketUrl);
      yield put(initialize());
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Impossible to connect to socket with error', error);
    }
  }
  return socket;
}

function* initSocket() {
  const { module } = yield select(getEntity);
  const websocketUrl = yield select(getWebsocketUrl);
  const canCreateSocket = websocketUrl && module !== MODULES.STOCK_VO && !PREVIEW;

  if (canCreateSocket) {
    const mySocket = yield call(getWS);
    if (mySocket) {
      const socketChannel = yield call(createSocketChannel, mySocket);

      let action;
      do {
        action = yield take(socketChannel);
        yield put(action);
      } while (action.type !== actionTypes.SOCKET_CLOSED);
    }
  }
}

function* afterClosed() {
  const hasTimeout = yield call(isConnectionTimeout);
  if (hasTimeout) {
    yield put(openRefreshPageModal());
  }
}

function* send({ payload: data }) {
  const isConnected = yield select(isConnectedSelector);
  const hasTimeout = yield call(isConnectionTimeout);
  if (isConnected && !hasTimeout) {
    const mySocket = yield call(getWS);
    mySocket?.send(data);
  }
}

export default function* websocketsSagas() {
  yield takeLatest(FETCH_ENTITY_SUCCESS, initSocket);
  yield takeLatest(actionTypes.SOCKET_SEND, send);
  yield takeLatest(actionTypes.SOCKET_CLOSED, afterClosed);
  yield takeLatest(actionTypes.SOCKET_INITIALIZED, keepAlive);
}
